/** tm/components/StepSettingModal.tsx — 단계 세부 설정 모달 (CollectPanel 스타일) */
import { useState, useCallback } from 'react';
import { C, T, R, Badge, Btn } from './ui';
import * as api from '../api';

/* ── 타입 ── */
interface Guide { type: 'link' | 'file'; label: string; url?: string; file?: File; fileName?: string }
interface CollectItem {
  id: string; label: string; type: string; required: boolean;
  sensitive?: boolean; desc?: string; options?: string[]; guides?: Guide[];
}
interface Props {
  stepIndex: number;
  stepName: string;
  stepType: string;
  settings?: Record<string, unknown>;
  aiPresets?: { id: string; name: string; type: string }[];
  collectTemplates?: { id: string; name: string; itemCount?: number }[];
  onSave: (settings: Record<string, unknown>) => void;
  onClose: () => void;
}

const TYPE_META: Record<string, { label: string; icon: string; color: string; bg: string }> = {
  work:     { label: '일반업무', icon: '⚙️', color: '#6B7280', bg: '#F3F4F6' },
  collect:  { label: '자료수집', icon: '📂', color: '#2563EB', bg: '#EFF6FF' },
  ai_questionnaire: { label: 'AI 질문지', icon: '🤖', color: '#7C3AED', bg: '#F3E8FF' },
  ai_bizplan: { label: 'AI 사업계획서', icon: '📊', color: '#7C3AED', bg: '#F3E8FF' },
  confirm:  { label: '검토', icon: '✅', color: '#059669', bg: '#ECFDF5' },
  report:   { label: '제출', icon: '📤', color: '#2E75B6', bg: '#E8F0FE' },
};

const TYPE_OPTIONS = [
  { v: 'file', icon: '📎', l: '파일' },
  { v: 'short_text', icon: '✏️', l: '단문' },
  { v: 'long_text', icon: '📝', l: '장문' },
  { v: 'radio', icon: '○', l: '객관식' },
  { v: 'checkbox', icon: '☑', l: '체크박스' },
];
const needsOptions = (t: string) => t === 'radio' || t === 'checkbox';

let _idCounter = 0;
const uid = () => `ci_${Date.now()}_${++_idCounter}`;

export default function StepSettingModal({ stepIndex, stepName, stepType, settings: init = {}, aiPresets = [], collectTemplates = [], onSave, onClose }: Props) {
  const typeInfo = TYPE_META[stepType] || TYPE_META.work;

  /* ── collect state ── */
  const [items, setItems] = useState<CollectItem[]>(() => (init.collectItems as CollectItem[] || []).map(i => ({ ...i, id: i.id || uid() })));
  const [baseTemplateId, setBaseTemplateId] = useState<string>((init.baseTemplateId as string) || '');
  const [loadingBase, setLoadingBase] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  // 템플릿 이어붙이기 (자료수집 앱 '템플릿 전체 추가'와 동등 기능)
  const [tplAddSel, setTplAddSel] = useState('');
  const [tplAddBusy, setTplAddBusy] = useState(false);
  const [tplAddMsg, setTplAddMsg] = useState('');
  // new-item form
  const [newLabel, setNewLabel] = useState('');
  const [newType, setNewType] = useState('file');
  const [newReq, setNewReq] = useState(true);
  const [newSensitive, setNewSensitive] = useState(false);
  const [newDesc, setNewDesc] = useState('');
  const [newOpts, setNewOpts] = useState<string[]>([]);
  const [newGuides, setNewGuides] = useState<Guide[]>([]);

  /* ── work / other state ── */
  const [description, setDescription] = useState((init.description as string) || '');
  const [checklist, setChecklist] = useState<{ text: string; done: boolean }[]>((init.checklist as { text: string; done: boolean }[]) || []);
  const [notes, setNotes] = useState((init.notes as string) || '');

  /* ── handlers ── */
  const updItem = useCallback((idx: number, patch: Partial<CollectItem>) => {
    setItems(p => p.map((x, j) => j === idx ? { ...x, ...patch } : x));
  }, []);

  const removeItem = useCallback((idx: number) => {
    setItems(p => p.filter((_, j) => j !== idx));
  }, []);

  const addItem = useCallback(() => {
    if (!newLabel.trim()) return;
    setItems(p => [...p, { id: uid(), label: newLabel.trim(), type: newType, required: newReq, sensitive: newSensitive, desc: newDesc, options: needsOptions(newType) ? newOpts : undefined, guides: newGuides.length ? newGuides : undefined }]);
    setNewLabel(''); setNewType('file'); setNewReq(true); setNewSensitive(false); setNewDesc(''); setNewOpts([]); setNewGuides([]);
  }, [newLabel, newType, newReq, newSensitive, newDesc, newOpts, newGuides]);

  // 자료수집 템플릿 유형 → TM 항목 유형 매핑 (text/text_long이 셀렉트에서 깨지던 문제 수정)
  const mapTplType = (t: string): string => t === 'text' ? 'short_text' : t === 'text_long' ? 'long_text' : (t || 'file');
  const fetchTplItems = async (templateId: string): Promise<CollectItem[]> => {
    const token = localStorage.getItem('token') || '';
    const res = await fetch(`/api/templates/${templateId}`, { headers: { Authorization: 'Bearer ' + token } });
    const json = await res.json();
    const tmplItems = json.data?.items || json.items || [];
    if (!Array.isArray(tmplItems)) return [];
    return tmplItems.map((it: Record<string, unknown>) => ({
      id: uid(),
      label: (it.label as string) || '',
      type: mapTplType(it.type as string),
      required: it.required !== false,
      sensitive: !!it.sensitive,
      desc: (it.description as string) || (it.desc as string) || '',
      options: it.options as string[] | undefined,
      guides: it.guides as Guide[] | undefined,
    }));
  };
  const handleBaseTemplateChange = async (templateId: string) => {
    setBaseTemplateId(templateId);
    if (!templateId) return;
    setLoadingBase(true);
    try {
      const mapped = await fetchTplItems(templateId);
      if (mapped.length > 0) setItems(mapped);
    } catch { /* ignore */ }
    finally { setLoadingBase(false); }
  };
  const appendTemplate = async () => {
    if (!tplAddSel || tplAddBusy) return;
    setTplAddBusy(true); setTplAddMsg('');
    try {
      const mapped = await fetchTplItems(tplAddSel);
      const existing = new Set(items.map(x => (x.label || '').trim()));
      const fresh = mapped.filter(x => !existing.has((x.label || '').trim()));
      const dup = mapped.length - fresh.length;
      if (fresh.length) setItems(prev => [...prev, ...fresh]);
      setTplAddMsg(`${fresh.length}개 추가${dup ? ` (동일 항목명 ${dup}개 제외)` : ''}`);
      setTplAddSel('');
    } catch { setTplAddMsg('불러오기 실패'); }
    finally { setTplAddBusy(false); }
  };

  const handleSave = () => {
    if (stepType === 'collect') {
      onSave({ collectItems: items, baseTemplateId });
    } else if (stepType === 'work') {
      onSave({ description, checklist });
    } else {
      onSave({ description, notes });
    }
  };

  /* ── shared inline styles ── */
  const pill = (active: boolean, activeColor: string = C.accent): React.CSSProperties => ({
    fontSize: T.xs, color: active ? '#fff' : C.muted, fontWeight: 600,
    background: active ? activeColor : C.bg, border: 'none', cursor: 'pointer',
    padding: '3px 10px', borderRadius: 10, transition: 'all .15s',
  });
  const inputSm: React.CSSProperties = {
    flex: 1, padding: '6px 10px', border: `1px solid ${C.border}`, borderRadius: R.sm,
    fontSize: T.xs, outline: 'none', boxSizing: 'border-box', background: '#fff',
  };
  const dashBtn: React.CSSProperties = {
    background: 'none', border: `1px dashed ${C.borderDk}`, borderRadius: 4,
    padding: '2px 8px', fontSize: T.xs, color: C.muted, cursor: 'pointer',
  };

  /* ── guide row renderer ── */
  const renderGuide = (g: Guide, gi: number, getGuides: () => Guide[], setGuides: (fn: (p: Guide[]) => Guide[]) => void) => (
    <div key={gi} style={{ display: 'flex', gap: 4, alignItems: 'center', marginTop: 4, padding: '3px 6px', background: C.bgAlt, borderRadius: 4, fontSize: T.xs }}>
      <span style={{ color: C.muted, fontSize: T.xs, minWidth: 28 }}>{g.type === 'link' ? '링크' : '파일'}</span>
      <input value={g.label || ''} onChange={e => { const v = e.target.value; setGuides(p => p.map((x, j) => j === gi ? { ...x, label: v } : x)); }} placeholder="버튼 문구" style={inputSm} />
      {g.type === 'link' && <input value={g.url || ''} onChange={e => { const v = e.target.value; setGuides(p => p.map((x, j) => j === gi ? { ...x, url: v } : x)); }} placeholder="https://..." style={inputSm} />}
      {g.type === 'file' && <span style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1 }}>
        {g.fileName ? <span style={{ fontSize: T.xs, color: C.green }}>{g.fileName}</span> : <input type="file" onChange={e => { const f = e.target.files?.[0]; if (f) setGuides(p => p.map((x, j) => j === gi ? { ...x, file: f, fileName: f.name } : x)); }} style={{ fontSize: T.xs, maxWidth: 150 }} />}
      </span>}
      <button onClick={() => setGuides(p => p.filter((_, j) => j !== gi))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: C.muted, fontSize: T.xs }}>✕</button>
    </div>
  );

  /* ── options editor ── */
  const renderOptions = (type: string, options: string[], setOpts: (opts: string[]) => void) => (
    <div style={{ marginTop: 6, padding: '8px 10px', background: '#fff', borderRadius: R.sm, border: `1px solid ${C.border}` }}>
      <div style={{ fontSize: T.xs, color: C.muted, marginBottom: 3, fontWeight: 600 }}>선택지 ({type === 'radio' ? '단일선택' : '복수선택'})</div>
      {options.map((opt, oi) => (
        <div key={oi} style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 3 }}>
          <span style={{ fontSize: T.xs, color: C.muted, minWidth: 18, textAlign: 'center' }}>{type === 'radio' ? '○' : '□'}</span>
          <input value={opt} onChange={e => { const v = e.target.value; setOpts(options.map((x, j) => j === oi ? v : x)); }} placeholder={`선택지 ${oi + 1}`} style={inputSm} />
          <button onClick={() => setOpts(options.filter((_, j) => j !== oi))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: C.muted, fontSize: T.xs }}>✕</button>
        </div>
      ))}
      <button onClick={() => setOpts([...options, ''])} style={dashBtn}>+ 선택지 추가</button>
    </div>
  );

  /* ══════════ RENDER ══════════ */
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} />
      <div style={{ position: 'relative', width: '100%', maxWidth: 700, maxHeight: '85vh', background: '#fff', borderRadius: 12, display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>

        {/* ── Header ── */}
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 10, background: `linear-gradient(135deg, #1B3A5C 0%, #2E75B6 100%)`, borderRadius: '12px 12px 0 0' }}>
          <span style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: T.md, fontWeight: 700, color: '#fff' }}>{stepIndex + 1}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: T.md, fontWeight: 700, color: '#fff' }}>{stepName || '단계 설정'}</div>
            <Badge bg={typeInfo.bg} color={typeInfo.color} style={{ marginTop: 2, fontSize: T.xs }}>{typeInfo.icon} {typeInfo.label}</Badge>
          </div>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 8, border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.15)', cursor: 'pointer', fontSize: T.sm, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>

        {/* ── Body ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>

          {/* ═══ COLLECT TYPE ═══ */}
          {stepType === 'collect' && (<div>
            {collectTemplates.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: T.xs, fontWeight: 700, color: C.sub, display: 'block', marginBottom: 4 }}>베이스 템플릿</label>
                <select value={baseTemplateId} onChange={e => handleBaseTemplateChange(e.target.value)}
                  disabled={loadingBase}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm, outline: 'none', background: '#fff', boxSizing: 'border-box', color: baseTemplateId ? C.text : C.muted }}>
                  <option value="">베이스 템플릿 선택 (선택사항)</option>
                  {collectTemplates.map(ct => (
                    <option key={ct.id} value={ct.id}>{ct.name}{ct.itemCount ? ` (${ct.itemCount}항목)` : ''}</option>
                  ))}
                </select>
                {loadingBase && <div style={{ fontSize: T.xs, color: C.muted, marginTop: 4 }}>항목 불러오는 중...</div>}
              </div>
            )}
            <div style={{ fontSize: T.sm, fontWeight: 700, color: C.text, marginBottom: 8 }}>수집 항목</div>
            {items.length === 0 && (
              <div style={{ padding: '6px 10px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: R.sm, marginBottom: 10, fontSize: T.xs, color: '#92400E' }}>⚠️ 최소 1개 이상 등록해주세요.</div>
            )}

            {/* existing items */}
            {items.map((it, i) => {
              const gs = it.guides || [];
              const linkCount = gs.filter(g => g.type === 'link').length;
              const fileCount = gs.filter(g => g.type === 'file').length;
              return (
                <div key={it.id} draggable
                  onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; setDragIdx(i); }}
                  onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOver(i); }}
                  onDragEnd={() => { setDragIdx(null); setDragOver(null); }}
                  onDrop={e => { e.preventDefault(); if (dragIdx !== null && dragIdx !== i) { setItems(p => { const arr = [...p]; const [moved] = arr.splice(dragIdx, 1); arr.splice(i, 0, moved); return arr; }); } setDragIdx(null); setDragOver(null); }}
                  style={{ padding: '10px 12px', borderRadius: 8, background: C.bgAlt, border: `1px solid ${C.border}`, marginBottom: 6, cursor: 'grab', opacity: dragIdx === i ? 0.4 : 1, borderTop: dragOver === i ? `2px solid ${C.accent}` : undefined, transition: 'opacity .15s' }}>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ cursor: 'grab', color: C.muted, fontSize: T.md }}>⠿</span>
                    <span style={{ flex: 1, fontSize: T.sm, fontWeight: 600, color: C.text }}>{it.label}</span>
                    <select value={it.type} onChange={e => updItem(i, { type: e.target.value })}
                      style={{ fontSize: T.xs, color: C.sub, background: C.bgAlt, padding: '3px 6px', borderRadius: 5, border: `1px solid ${C.border}`, cursor: 'pointer', outline: 'none' }}>
                      {TYPE_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.icon} {o.l}</option>)}
                    </select>
                    <button onClick={() => updItem(i, { required: !it.required })} style={pill(it.required)}>{it.required ? '필수 ✓' : '선택'}</button>
                    <button onClick={() => updItem(i, { sensitive: !it.sensitive })} style={pill(!!it.sensitive, '#D97706')}>{it.sensitive ? '민감 ✓' : '일반'}</button>
                    <button onClick={() => removeItem(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: C.muted, fontSize: T.sm }}>✕</button>
                  </div>

                  <input value={it.desc || ''} onChange={e => updItem(i, { desc: e.target.value })} placeholder="설명글 (선택사항)"
                    style={{ width: '100%', marginTop: 6, padding: '5px 8px', border: `1px solid ${C.border}`, borderRadius: 5, fontSize: T.xs, color: C.sub, outline: 'none', boxSizing: 'border-box', background: '#fff' }} />

                  {/* guides */}
                  {gs.map((g, gi) => renderGuide(g, gi, () => gs, fn => updItem(i, { guides: fn(gs) })))}

                  {/* options for radio/checkbox */}
                  {needsOptions(it.type) && renderOptions(it.type, it.options || [], opts => updItem(i, { options: opts }))}

                  <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                    {linkCount < 2 && <button onClick={() => updItem(i, { guides: [...gs, { type: 'link', label: '', url: '' }] })} style={dashBtn}>+ 가이드 링크</button>}
                    {fileCount < 2 && <button onClick={() => updItem(i, { guides: [...gs, { type: 'file', label: '', fileName: '' }] })} style={dashBtn}>+ 가이드 파일</button>}
                  </div>
                </div>
              );
            })}

            {/* ── new item form ── */}
            <div style={{ marginTop: 10, padding: 12, background: C.accentLt, borderRadius: 8 }}>
              <div style={{ fontSize: T.xs, fontWeight: 600, color: C.accent, marginBottom: 8 }}>새 항목 추가</div>
              {collectTemplates.length > 0 && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 10, paddingBottom: 10, borderBottom: `1px dashed ${C.border}` }}>
                  <span style={{ fontSize: T.xs, fontWeight: 600, color: C.sub, whiteSpace: 'nowrap' }}>📋 템플릿에서 추가</span>
                  <select value={tplAddSel} onChange={e => { setTplAddSel(e.target.value); setTplAddMsg(''); }}
                    style={{ flex: 1, padding: '9px 8px', border: `1.5px solid ${C.border}`, borderRadius: 7, fontSize: T.sm, background: '#fff', outline: 'none', color: tplAddSel ? C.text : C.muted }}>
                    <option value="">템플릿 선택 — 항목 전체를 이어서 추가</option>
                    {collectTemplates.map(ct => <option key={ct.id} value={ct.id}>{ct.name}{ct.itemCount ? ` (${ct.itemCount}항목)` : ''}</option>)}
                  </select>
                  <button onClick={appendTemplate} disabled={!tplAddSel || tplAddBusy}
                    style={{ padding: '9px 14px', borderRadius: 7, border: `1px solid ${tplAddSel && !tplAddBusy ? C.accent : C.border}`, background: tplAddSel && !tplAddBusy ? C.accentLt : '#fff', color: tplAddSel && !tplAddBusy ? C.accent : C.muted, fontSize: T.xs, fontWeight: 600, cursor: tplAddSel && !tplAddBusy ? 'pointer' : 'default', whiteSpace: 'nowrap' }}>
                    {tplAddBusy ? '추가 중…' : '전체 추가'}</button>
                  {tplAddMsg && <span style={{ fontSize: T.xs, color: C.sub, whiteSpace: 'nowrap' }}>{tplAddMsg}</span>}
                </div>
              )}
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                <input value={newLabel} onChange={e => setNewLabel(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addItem(); }} placeholder="항목명"
                  style={{ flex: 1, padding: '10px 12px', border: `1.5px solid ${C.border}`, borderRadius: 7, fontSize: T.md, outline: 'none', boxSizing: 'border-box', background: '#fff' }} />
                <select value={newType} onChange={e => setNewType(e.target.value)}
                  style={{ padding: '10px 20px 10px 8px', border: `1.5px solid ${C.border}`, borderRadius: 7, fontSize: T.sm, background: '#fff' }}>
                  {TYPE_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.icon} {o.l}</option>)}
                </select>
                <button onClick={() => setNewReq(!newReq)} style={pill(newReq)}>{newReq ? '필수 ✓' : '선택'}</button>
                <button onClick={() => setNewSensitive(!newSensitive)} style={pill(newSensitive, '#D97706')}>{newSensitive ? '민감 ✓' : '일반'}</button>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                <input value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="설명글 (선택사항)"
                  style={{ flex: 1, padding: '10px 10px', border: `1.5px solid ${C.border}`, borderRadius: 7, fontSize: T.sm, outline: 'none', boxSizing: 'border-box', background: '#fff' }} />
                {needsOptions(newType) && <button onClick={() => setNewOpts(p => [...p, ''])} style={{ ...dashBtn, padding: '10px 10px', fontSize: T.xs }}>+ 선택지</button>}
                {newGuides.filter(g => g.type === 'link').length < 2 && <button onClick={() => setNewGuides(p => [...p, { type: 'link', label: '', url: '' }])} style={{ ...dashBtn, padding: '10px 10px', fontSize: T.xs }}>+ 가이드 링크</button>}
                {newGuides.filter(g => g.type === 'file').length < 2 && <button onClick={() => setNewGuides(p => [...p, { type: 'file', label: '', fileName: '' }])} style={{ ...dashBtn, padding: '10px 10px', fontSize: T.xs }}>+ 가이드 파일</button>}
                <button onClick={addItem} style={{ padding: '10px 14px', borderRadius: 7, border: 'none', color: '#fff', fontSize: T.xs, fontWeight: 600, cursor: 'pointer', background: C.accent, whiteSpace: 'nowrap' }}>항목 추가</button>
              </div>
              {newOpts.length > 0 && renderOptions(newType, newOpts, setNewOpts)}
              {newGuides.map((g, gi) => renderGuide(g, gi, () => newGuides, setNewGuides))}
            </div>
          </div>)}

          {/* ═══ WORK TYPE ═══ */}
          {stepType === 'work' && (<div>
            <label style={{ fontSize: T.xs, fontWeight: 700, color: C.sub, display: 'block', marginBottom: 4 }}>업무 설명</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="이 단계의 업무 설명"
              style={{ width: '100%', minHeight: 80, padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: T.md, outline: 'none', boxSizing: 'border-box', resize: 'vertical' }} />
            <div style={{ marginTop: 12 }}>
              <label style={{ fontSize: T.xs, fontWeight: 700, color: C.sub, display: 'block', marginBottom: 6 }}>체크리스트</label>
              {checklist.map((item, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <span style={{ fontSize: T.xs, color: C.muted, width: 16, textAlign: 'center' }}>{idx + 1}</span>
                  <input value={item.text} onChange={e => { const v = e.target.value; setChecklist(p => p.map((x, j) => j === idx ? { ...x, text: v } : x)); }} placeholder="체크리스트 항목"
                    style={{ flex: 1, padding: '6px 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.xs, outline: 'none' }} />
                  <button onClick={() => setChecklist(p => p.filter((_, j) => j !== idx))} style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer', fontSize: T.sm }}>✕</button>
                </div>
              ))}
              <button onClick={() => setChecklist(p => [...p, { text: '', done: false }])}
                style={{ width: '100%', padding: 6, borderRadius: R.sm, border: `1.5px dashed ${C.border}`, background: 'transparent', fontSize: T.xs, color: C.muted, cursor: 'pointer' }}>
                + 체크리스트 항목 추가
              </button>
            </div>
          </div>)}

          {/* ═══ OTHER TYPES ═══ */}
          {stepType !== 'collect' && stepType !== 'work' && (<div>
            <label style={{ fontSize: T.xs, fontWeight: 700, color: C.sub, display: 'block', marginBottom: 4 }}>설명</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="이 단계에 대한 설명"
              style={{ width: '100%', minHeight: 80, padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: T.md, outline: 'none', boxSizing: 'border-box', resize: 'vertical' }} />
            <label style={{ fontSize: T.xs, fontWeight: 700, color: C.sub, display: 'block', marginTop: 12, marginBottom: 4 }}>비고</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="추가 메모"
              style={{ width: '100%', minHeight: 60, padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: T.md, outline: 'none', boxSizing: 'border-box', resize: 'vertical' }} />
          </div>)}
        </div>

        {/* ── Footer ── */}
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: 8 }}>
          <Btn variant="secondary" onClick={onClose} style={{ flex: 1 }}>취소</Btn>
          <Btn onClick={handleSave} style={{ flex: 2 }}>저장</Btn>
        </div>
      </div>
    </div>
  );
}
